/**
 * Unified WebSocket Adapter
 * Единый интерфейс для WebSocket соединений с поддержкой всех режимов:
 * - Legacy AngularJS sharedWSManager
 * - Standard WebSocket с JSON протоколом
 * - Enhanced WebSocket с binary protocol и compression
 *
 * Единственный WebSocket composable (заменил useWebSocket.ts / WebSocketManager.ts /
 * EnhancedWebSocketManager.ts — удалены 2026-04-19).
 */

import { computed, onUnmounted, ref, type ComputedRef } from 'vue';
import { logger } from '../utils/logger';

// ============== Types ==============

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export enum MessageType {
  PING = 0x01, PONG = 0x02, AUTH = 0x03,
  SUBSCRIBE = 0x04, UNSUBSCRIBE = 0x05,
  CHAT_MESSAGE = 0x10, MESSAGE_READ = 0x11, TYPING = 0x12,
  NAVIGATION_UPDATE = 0x20, NOTIFICATION_READ = 0x21,
  PRESENCE_UPDATE = 0x30, ONLINE_STATUS = 0x31,
  SPOTLIGHT_UPDATE = 0x40
}

export interface UnifiedWebSocketMessage {
  type: string | MessageType;
  data?: any;
  channel?: string;
  timestamp?: number;
}

export interface WebSocketMetrics {
  messagesSent: number;
  messagesReceived: number;
  bytesTransferred: number;
  compressionRatio: number;
  averageLatency: number;
  errorCount: number;
  reconnectCount: number;
}

export interface UnifiedWebSocketConfig {
  url?: string;
  autoReconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
  enableBinaryProtocol?: boolean;
  enableCompression?: boolean;
  compressionThreshold?: number;
  metricsEnabled?: boolean;
  preferLegacy?: boolean; // Prefer AngularJS sharedWSManager if available
}

export interface UnifiedWebSocketEvents {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
  onMessage?: (message: UnifiedWebSocketMessage) => void;
  onMetricsUpdate?: (metrics: WebSocketMetrics) => void;
}

export interface UnifiedWebSocketComposable {
  status: ComputedRef<ConnectionStatus>;
  isConnected: ComputedRef<boolean>;
  isConnecting: ComputedRef<boolean>;
  isDisconnected: ComputedRef<boolean>;
  hasError: ComputedRef<boolean>;
  metrics: ComputedRef<WebSocketMetrics | undefined>;
  connect: () => void;
  disconnect: () => void;
  send: (message: UnifiedWebSocketMessage | string) => void;
  subscribe: (channel: string) => void;
  unsubscribe: (channel: string) => void;
  sendBinary?: (type: number, data: ArrayBuffer | string, channel?: string) => void;
  sendChatMessage: (data: string, channel?: string) => void;
  sendTyping: (isTyping: boolean, conversationId: string) => void;
  setEvents: (events: UnifiedWebSocketEvents) => void;
}

// ============== Constants ==============

const DEFAULT_CONFIG: Required<Omit<UnifiedWebSocketConfig, 'url'>> = {
  autoReconnect: true,
  reconnectInterval: 1000, // базовая задержка; scheduleReconnect применяет exponential backoff
  maxReconnectAttempts: 10,
  heartbeatInterval: 30000,
  enableBinaryProtocol: false, // NOTE: Binary protocol не реализован на backend
  enableCompression: false,    // NOTE: Compression не реализован на backend
  compressionThreshold: 1024,
  metricsEnabled: true,
  preferLegacy: true
};

// ============== Singleton State ==============

let unifiedInstance: UnifiedWebSocketManager | null = null;
let consumerCount = 0;

// ============== Helper Functions ==============

const getWebSocketUrl = (config: UnifiedWebSocketConfig): string => {
  if (config.url) return config.url;

  const metaToken = document.querySelector('meta[name="websocket-token"]')?.getAttribute('content');
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const base = `${scheme}//${window.location.host}/ws`;

  if (!metaToken) return base;

  let clientId = (window as any).__ws_client_id_cache;
  if (!clientId) {
    try {
      clientId = localStorage.getItem('ws_client_id');
    } catch { /* ignore */ }
  }

  if (!clientId) {
    clientId = 'client_' + Math.random().toString(36).slice(2, 11);
    try {
      localStorage.setItem('ws_client_id', clientId);
    } catch { /* ignore */ }
    (window as any).__ws_client_id_cache = clientId;
  }

  const params = new URLSearchParams();
  params.set('token', metaToken);
  params.set('v', '2.0'); // Binary protocol не реализован на backend
  params.set('client_id', clientId);

  return `${base}?${params.toString()}`;
};

const stringToMessageType = (type: string): MessageType => {
  const map: Record<string, MessageType> = {
    ping: MessageType.PING, pong: MessageType.PONG,
    auth: MessageType.AUTH, subscribe: MessageType.SUBSCRIBE,
    unsubscribe: MessageType.UNSUBSCRIBE, chat: MessageType.CHAT_MESSAGE,
    message_read: MessageType.MESSAGE_READ, typing: MessageType.TYPING,
    navigation_update: MessageType.NAVIGATION_UPDATE,
    notification_read: MessageType.NOTIFICATION_READ,
    presence_update: MessageType.PRESENCE_UPDATE,
    online_status: MessageType.ONLINE_STATUS,
    spotlight_update: MessageType.SPOTLIGHT_UPDATE
  };
  return map[type] || MessageType.CHAT_MESSAGE;
};

const messageTypeToString = (type: MessageType): string => {
  const map: Record<MessageType, string> = {
    [MessageType.PING]: 'ping', [MessageType.PONG]: 'pong',
    [MessageType.AUTH]: 'auth', [MessageType.SUBSCRIBE]: 'subscribe',
    [MessageType.UNSUBSCRIBE]: 'unsubscribe', [MessageType.CHAT_MESSAGE]: 'chat',
    [MessageType.MESSAGE_READ]: 'message_read', [MessageType.TYPING]: 'typing',
    [MessageType.NAVIGATION_UPDATE]: 'navigation_update',
    [MessageType.NOTIFICATION_READ]: 'notification_read',
    [MessageType.PRESENCE_UPDATE]: 'presence_update',
    [MessageType.ONLINE_STATUS]: 'online_status',
    [MessageType.SPOTLIGHT_UPDATE]: 'spotlight_update'
  };
  return map[type] || 'chat';
};

// ============== Legacy Adapter ==============

class LegacyWebSocketAdapter {
  private events: UnifiedWebSocketEvents = {};
  private unsubscribers: Array<() => void> = [];

  constructor() {
    this.bindToLegacyEvents();
  }

  private bindToLegacyEvents(): void {
    const $rootScope = (window as any).$rootScope;
    
    // Try AngularJS $rootScope first
    if ($rootScope) {
      const bind = (event: string, handler: (p: any) => void) => {
        const unsub = $rootScope.$on(event, (_: any, payload: any) => handler(payload));
        if (typeof unsub === 'function') this.unsubscribers.push(unsub);
      };

      bind('websocketMessageService:readReceipt', (p) => {
        this.events.onMessage?.({ type: 'message_read', data: p });
      });

      bind('websocketMessageService:chatMessage', (p) => {
        this.events.onMessage?.({ type: 'chat', data: p });
      });

      bind('websocketMessageService:typing', (p) => {
        this.events.onMessage?.({ type: 'typing', data: p });
      });

      bind('websocketMessageService:blockChanged', (p) => {
        this.events.onMessage?.({ type: 'user_block_changed', data: p });
        window.dispatchEvent(new CustomEvent('user_block_changed', { detail: p }));
      });
      
      logger.log('LegacyWebSocketAdapter: Bound to $rootScope events');
      return;
    }
    
    // Fallback: use window.webSocketHelpers directly (when AngularJS is not loaded)
    const webSocketHelpers = (window as any).webSocketHelpers;
    if (webSocketHelpers?.on) {
      logger.log('LegacyWebSocketAdapter: Binding to webSocketHelpers.on (no AngularJS)');

      // Сохраняем handler references, чтобы корректно отвязать в destroy()
      // и не плодить дубликаты listeners при reconnect composable'а.
      const bindHelper = (event: string, handler: (p: any) => void) => {
        webSocketHelpers.on(event, handler);
        if (typeof webSocketHelpers.off === 'function') {
          this.unsubscribers.push(() => webSocketHelpers.off(event, handler));
        }
      };

      bindHelper('chat', (payload: any) => {
        logger.log('LegacyWebSocketAdapter: Received chat via webSocketHelpers', payload);
        this.events.onMessage?.({ type: 'chat', ...payload });
      });

      bindHelper('message_read', (payload: any) => {
        logger.log('LegacyWebSocketAdapter: Received message_read via webSocketHelpers', payload);
        this.events.onMessage?.({ type: 'message_read', ...payload });
      });

      bindHelper('typing', (payload: any) => {
        logger.log('LegacyWebSocketAdapter: Received typing via webSocketHelpers', payload);
        this.events.onMessage?.({ type: 'typing', ...payload });
      });

      bindHelper('user_block_changed', (payload: any) => {
        logger.log('LegacyWebSocketAdapter: Received blockChanged via webSocketHelpers', payload);
        this.events.onMessage?.({ type: 'user_block_changed', ...payload });
        window.dispatchEvent(new CustomEvent('user_block_changed', { detail: payload }));
      });
    } else {
      logger.warn('LegacyWebSocketAdapter: No event system available ($rootScope or webSocketHelpers)');
    }
  }

  setEvents(events: UnifiedWebSocketEvents): void {
    this.events = { ...this.events, ...events };
  }

  connect(): void {
    if (window.sharedWSManager?.connect) {
      window.sharedWSManager.connect();
    }
    this.events.onConnect?.();
  }

  disconnect(): void {
    if (window.sharedWSManager?.disconnect) {
      window.sharedWSManager.disconnect();
    }
    this.events.onDisconnect?.();
  }

  send(message: UnifiedWebSocketMessage): void {
    if (window.sharedWSManager?.send) {
      window.sharedWSManager.send(message);
    }
  }

  destroy(): void {
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers = [];
  }

  getStatus(): ConnectionStatus {
    const legacyStatus = window.sharedWSManager?.getStatus?.();
    if (legacyStatus === 'connected') return 'connected';
    if (legacyStatus === 'connecting') return 'connecting';
    return 'disconnected';
  }
}

// ============== Native WebSocket Manager ==============

class NativeWebSocketManager {
  private ws: WebSocket | null = null;
  private status = ref<ConnectionStatus>('disconnected');
  private readonly config: Required<Omit<UnifiedWebSocketConfig, 'url'>> & { url?: string };
  private events: UnifiedWebSocketEvents = {};
  private reconnectAttempts = 0;
  private reconnectBlockedUntil = 0;
  private heartbeatTimer: number | null = null;
  private messageQueue: UnifiedWebSocketMessage[] = [];
  private metrics: WebSocketMetrics = {
    messagesSent: 0, messagesReceived: 0, bytesTransferred: 0,
    compressionRatio: 1, averageLatency: 0, errorCount: 0, reconnectCount: 0
  };

  constructor(config: UnifiedWebSocketConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  getStatus(): ConnectionStatus { return this.status.value; }
  getStatusRef() { return computed(() => this.status.value); }
  getMetrics(): WebSocketMetrics { return { ...this.metrics }; }
  getMetricsRef() { return computed(() => this.getMetrics()); }

  setEvents(events: UnifiedWebSocketEvents): void {
    this.events = { ...this.events, ...events };
  }

  connect(): Promise<void> {
    return new Promise((resolve) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      // Подключение уже запущено — не создаём второй WebSocket
      if (this.ws?.readyState === WebSocket.CONNECTING) {
        resolve();
        return;
      }

      this.status.value = 'connecting';

      try {
        const wsUrl = getWebSocketUrl(this.config);
        const protocols = this.config.enableBinaryProtocol ? ['json', 'binary'] : undefined;

        this.ws = new WebSocket(wsUrl, protocols);
        if (this.config.enableBinaryProtocol) {
          this.ws.binaryType = 'arraybuffer';
        }

        this.ws.onopen = () => {
          this.status.value = 'connected';
          this.reconnectAttempts = 0;
          this.reconnectBlockedUntil = 0;
          this.startHeartbeat();
          this.processMessageQueue();
          this.events.onConnect?.();
          resolve();
        };

        this.ws.onclose = (event) => {
          this.status.value = 'disconnected';
          this.stopHeartbeat();
          this.events.onDisconnect?.();

          // Фатальные коды: не пытаемся переподключаться
          const FATAL_CLOSE_CODES = [1002, 1008, 1011];
          const isFatal = FATAL_CLOSE_CODES.includes(event.code);

          if (this.config.autoReconnect && event.code !== 1000 && !isFatal) {
            this.scheduleReconnect(event.code);
          } else if (isFatal) {
            logger.warn(`WebSocket: Fatal close code ${event.code}, disabling autoReconnect`);
            this.config.autoReconnect = false;
          }
        };

        this.ws.onerror = () => {
          this.status.value = 'error';
          this.metrics.errorCount++;
          this.events.onError?.(new Error('WebSocket error'));
          resolve();
        };

        this.ws.onmessage = (event) => this.handleMessage(event);

      } catch {
        this.status.value = 'error';
        resolve();
      }
    });
  }

  disconnect(): void {
    this.config.autoReconnect = false;
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close(1000, 'Disconnected by user');
      this.ws = null;
    }
    this.messageQueue = [];
    this.reconnectAttempts = 0;
  }

  // Защитный лимит offline-очереди: не больше 100 сообщений.
  // Если превысили — выкидываем самое старое (FIFO drop), чтобы не съесть память
  // при длительном дисконнекте и не захлебнуться при reconnect.
  private static readonly MAX_QUEUE_SIZE = 100;

  send(message: UnifiedWebSocketMessage | string): void {
    const msg: UnifiedWebSocketMessage = typeof message === 'string'
      ? { type: 'chat', data: message, timestamp: Date.now() }
      : { ...message, timestamp: message.timestamp || Date.now() };

    if (this.ws?.readyState === WebSocket.OPEN) {
      const payload = this.config.enableBinaryProtocol
        ? this.createBinaryMessage(msg)
        : JSON.stringify(msg);
      this.ws.send(payload);
      this.metrics.messagesSent++;
      this.updateMetrics();
    } else {
      if (this.messageQueue.length >= NativeWebSocketManager.MAX_QUEUE_SIZE) {
        const dropped = this.messageQueue.shift();
        logger.warn('WebSocket: offline queue overflow, dropping oldest message', dropped?.type);
      }
      this.messageQueue.push(msg);
    }
  }

  subscribe(channel: string): void {
    this.send({ type: 'subscribe', channel, timestamp: Date.now() });
  }

  unsubscribe(channel: string): void {
    this.send({ type: 'unsubscribe', channel, timestamp: Date.now() });
  }

  private handleMessage(event: MessageEvent): void {
    try {
      let message: UnifiedWebSocketMessage;

      if (event.data instanceof ArrayBuffer && this.config.enableBinaryProtocol) {
        message = this.parseBinaryMessage(event.data);
      } else {
        message = JSON.parse(event.data);
      }

      this.metrics.messagesReceived++;
      if (message.timestamp) {
        const latency = Date.now() - message.timestamp;
        if (latency > 0 && latency < 60000) {
          this.metrics.averageLatency = this.metrics.averageLatency * 0.9 + latency * 0.1;
        }
      }

      // Обработка rate_limit_exceeded от сервера
      if (message.type === 'rate_limit_exceeded') {
        const retryAfterSeconds = Number((message.data as any)?.retry_after || 0);
        let retryAfterMs = 60_000;
        if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
          retryAfterMs = retryAfterSeconds * 1000;
        }
        this.reconnectBlockedUntil = Date.now() + retryAfterMs;
        logger.warn(`WebSocket: rate_limit_exceeded received. Reconnect blocked for ${retryAfterMs}ms`);
      }

      this.events.onMessage?.(message);
    } catch (err) {
      this.metrics.errorCount++;
      logger.error('Failed to parse WebSocket message:', err);
    }
  }

  private createBinaryMessage(message: UnifiedWebSocketMessage): ArrayBuffer {
    const encoder = new TextEncoder();
    const type = typeof message.type === 'string' ? stringToMessageType(message.type) : message.type;
    const dataStr = typeof message.data === 'string' ? message.data : JSON.stringify(message.data);
    const dataBytes = encoder.encode(dataStr);
    const channelBytes = message.channel ? encoder.encode(message.channel) : new Uint8Array(0);

    const headerSize = 16;
    const totalSize = headerSize + channelBytes.length + dataBytes.length;
    const buffer = new ArrayBuffer(totalSize);
    const view = new DataView(buffer);
    let offset = 0;

    view.setUint8(offset++, type);
    view.setUint8(offset++, 0); // flags
    view.setBigUint64(offset, BigInt(message.timestamp || Date.now()));
    offset += 8;
    view.setUint16(offset, channelBytes.length);
    offset += 2;
    view.setUint32(offset, dataBytes.length);
    offset += 4;

    new Uint8Array(buffer, offset, channelBytes.length).set(channelBytes);
    offset += channelBytes.length;
    new Uint8Array(buffer, offset, dataBytes.length).set(dataBytes);

    this.metrics.bytesTransferred += totalSize;
    return buffer;
  }

  private parseBinaryMessage(buffer: ArrayBuffer): UnifiedWebSocketMessage {
    const view = new DataView(buffer);
    let offset = 0;

    const type = view.getUint8(offset++) as MessageType;
    offset++; // skip flags
    const timestamp = Number(view.getBigUint64(offset));
    offset += 8;
    const channelLength = view.getUint16(offset);
    offset += 2;
    const dataLength = view.getUint32(offset);
    offset += 4;

    let channel: string | undefined;
    if (channelLength > 0) {
      channel = new TextDecoder().decode(new Uint8Array(buffer, offset, channelLength));
      offset += channelLength;
    }

    let data: any;
    if (dataLength > 0) {
      const dataStr = new TextDecoder().decode(new Uint8Array(buffer, offset, dataLength));
      try {
        data = JSON.parse(dataStr);
      } catch {
        data = dataStr;
      }
    }

    this.metrics.bytesTransferred += buffer.byteLength;
    return { type: messageTypeToString(type), data, channel, timestamp };
  }

  private startHeartbeat(): void {
    if (this.config.heartbeatInterval > 0) {
      this.heartbeatTimer = window.setInterval(() => {
        this.send({ type: MessageType.PING, timestamp: Date.now() });
      }, this.config.heartbeatInterval);
    }
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(closeCode: number | null = null): void {
    // Проверяем блокировку reconnect после rate_limit_exceeded
    if (this.reconnectBlockedUntil && Date.now() < this.reconnectBlockedUntil) {
      const waitMs = this.reconnectBlockedUntil - Date.now();
      logger.warn(`WebSocket: Reconnect blocked due to rate limit. Retrying in ${Math.round(waitMs / 1000)}s`);
      setTimeout(() => this.scheduleReconnect(closeCode), waitMs);
      return;
    }

    if (this.reconnectAttempts < this.config.maxReconnectAttempts) {
      this.reconnectAttempts++;
      this.metrics.reconnectCount++;
      // Exponential backoff с jitter: base * 2^(attempt-1), cap 60s.
      // Это снижает нагрузку на сервер при массовой потере коннекта
      // (network blip → все клиенты реконнектят одновременно).
      let base = this.config.reconnectInterval;
      if (closeCode === 1013) {
        base = Math.max(base, 8000); // Connection limit: минимум 8 сек
      }
      const exp = Math.min(base * Math.pow(2, this.reconnectAttempts - 1), 60_000);
      const jitter = Math.floor(Math.random() * 1000);
      const delay = exp + jitter;
      logger.log(`WebSocket: reconnect attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts} in ${delay}ms`);
      setTimeout(() => this.connect(), delay);
    } else {
      logger.warn(`WebSocket: max reconnect attempts (${this.config.maxReconnectAttempts}) reached, giving up`);
    }
  }

  private processMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const msg = this.messageQueue.shift();
      if (msg) this.send(msg);
    }
  }

  private updateMetrics(): void {
    if (this.config.metricsEnabled) {
      this.events.onMetricsUpdate?.(this.metrics);
    }
  }

  destroy(): void {
    this.disconnect();
    this.events = {};
    this.messageQueue = [];
  }
}

// ============== Unified Manager ==============

class UnifiedWebSocketManager {
  private readonly config: UnifiedWebSocketConfig;
  private readonly legacyAdapter: LegacyWebSocketAdapter | null = null;
  private readonly nativeManager: NativeWebSocketManager | null = null;
  private readonly useLegacy: boolean;

  constructor(config: UnifiedWebSocketConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.useLegacy = !!(this.config.preferLegacy && window.sharedWSManager);

    if (this.useLegacy) {
      this.legacyAdapter = new LegacyWebSocketAdapter();
    } else {
      this.nativeManager = new NativeWebSocketManager(this.config);
    }

    logger.log('UnifiedWebSocket: Using', this.useLegacy ? 'legacy adapter' : 'native manager');
  }

  get status(): ComputedRef<ConnectionStatus> {
    return this.useLegacy
      ? computed(() => this.legacyAdapter?.getStatus() || 'disconnected')
      : this.nativeManager!.getStatusRef();
  }

  get metrics(): ComputedRef<WebSocketMetrics | undefined> {
    return this.useLegacy
      ? computed(() => undefined)
      : this.nativeManager!.getMetricsRef();
  }

  setEvents(events: UnifiedWebSocketEvents): void {
    if (this.useLegacy) {
      this.legacyAdapter?.setEvents(events);
    } else {
      this.nativeManager?.setEvents(events);
    }
  }

  connect(): void {
    if (this.useLegacy) {
      this.legacyAdapter?.connect();
    } else {
      this.nativeManager?.connect().catch(() => {});
    }
  }

  disconnect(): void {
    if (this.useLegacy) {
      this.legacyAdapter?.disconnect();
    } else {
      this.nativeManager?.disconnect();
    }
  }

  send(message: UnifiedWebSocketMessage | string): void {
    if (this.useLegacy) {
      this.legacyAdapter?.send(typeof message === 'string' ? { type: 'chat', data: message } : message);
    } else {
      this.nativeManager?.send(message);
    }
  }

  subscribe(channel: string): void {
    this.send({ type: 'subscribe', channel, timestamp: Date.now() });
  }

  unsubscribe(channel: string): void {
    this.send({ type: 'unsubscribe', channel, timestamp: Date.now() });
  }

  sendBinary(type: number, data: ArrayBuffer | string, channel?: string): void {
    if (!this.useLegacy && this.config.enableBinaryProtocol) {
      this.nativeManager?.send({ type, data, channel, timestamp: Date.now() });
    }
  }

  sendChatMessage(data: string, channel?: string): void {
    this.send({ type: 'chat', data, channel, timestamp: Date.now() });
  }

  sendTyping(isTyping: boolean, conversationId: string): void {
    this.send({
      type: 'typing',
      data: JSON.stringify({ is_typing: isTyping, conversation_id: conversationId }),
      channel: 'typing',
      timestamp: Date.now()
    });
  }

  destroy(): void {
    this.legacyAdapter?.destroy();
    this.nativeManager?.destroy();
  }
}

// ============== Composable ==============

export function useUnifiedWebSocket(config?: UnifiedWebSocketConfig): UnifiedWebSocketComposable {
  if (!unifiedInstance) {
    unifiedInstance = new UnifiedWebSocketManager(config);
  }

  consumerCount++;

  onUnmounted(() => {
    consumerCount--;
    if (consumerCount <= 0) {
      unifiedInstance?.destroy();
      unifiedInstance = null;
      consumerCount = 0;
    }
  });

  const manager = unifiedInstance;

  return {
    status: manager.status,
    isConnected: computed(() => manager.status.value === 'connected'),
    isConnecting: computed(() => manager.status.value === 'connecting'),
    isDisconnected: computed(() => manager.status.value === 'disconnected'),
    hasError: computed(() => manager.status.value === 'error'),
    metrics: manager.metrics,
    connect: () => manager.connect(),
    disconnect: () => manager.disconnect(),
    send: (msg) => manager.send(msg),
    subscribe: (ch) => manager.subscribe(ch),
    unsubscribe: (ch) => manager.unsubscribe(ch),
    sendBinary: (type, data, channel) => manager.sendBinary(type, data, channel),
    sendChatMessage: (data, channel) => manager.sendChatMessage(data, channel),
    sendTyping: (isTyping, convId) => manager.sendTyping(isTyping, convId),
    setEvents: (events) => manager.setEvents(events)
  };
}

// Legacy exports for backward compatibility
export { useUnifiedWebSocket as useWebSocket };
export default useUnifiedWebSocket;

// Type augmentations
declare global {
  interface Window {
    sharedWSManager?: {
      connect?: () => void;
      disconnect?: () => void;
      send?: (msg: any) => void;
      getStatus?: () => string;
    };
    __ws_client_id_cache?: string;
  }
}
